import { parse as parse } from 'date-fns';
import { getHours as getHours } from 'date-fns';
import { getMinutes as getMinutes } from 'date-fns';
import { differenceInMinutes as differenceInMinutes } from 'date-fns';
import timeZoneData from './timezones.json';

export const defaultTimeSlot = [
  { day: 0, intervals: [], valid: true, openAllDay: false },
  { day: 1, intervals: [], valid: true, openAllDay: false },
  { day: 2, intervals: [], valid: true, openAllDay: false },
  { day: 3, intervals: [], valid: true, openAllDay: false },
  { day: 4, intervals: [], valid: true, openAllDay: false },
  { day: 5, intervals: [], valid: true, openAllDay: false },
  { day: 6, intervals: [], valid: true, openAllDay: false },
];

const pad = n => String(n).padStart(2, '0');

export const generateTimeSlots = (step = 15) => {
  const slots = [];
  for (let h = 0; h < 24; h += 1) {
    for (let m = 0; m < 60; m += step) {
      slots.push(`${pad(h)}:${pad(m)}`);
    }
  }
  if (!slots.includes('23:59')) slots.push('23:59');
  return slots;
};

export const getTime = (hour, minute) => `${pad(hour)}:${pad(minute)}`;

const parseTimeSlotsJson = raw => {
  if (!raw) return [];
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const toInterval = slot => ({
  from: getTime(slot.open_hour, slot.open_minutes),
  to: getTime(slot.close_hour, slot.close_minutes),
});

const intervalsAreValid = intervals => {
  if (!intervals.length) return true;
  const normalized = intervals.map(interval => {
    const fromDate = parse(interval.from, 'HH:mm', new Date());
    const toDate = parse(interval.to, 'HH:mm', new Date());
    const fromMinutes = getHours(fromDate) * 60 + getMinutes(fromDate);
    const toMinutes = getHours(toDate) * 60 + getMinutes(toDate);
    return { fromMinutes, toMinutes };
  });

  if (normalized.some(slot => slot.toMinutes <= slot.fromMinutes)) {
    return false;
  }

  const sorted = [...normalized].sort((a, b) => a.fromMinutes - b.fromMinutes);
  for (let i = 1; i < sorted.length; i += 1) {
    if (sorted[i].fromMinutes < sorted[i - 1].toMinutes) {
      return false;
    }
  }
  return true;
};

export const timeSlotParse = timeSlots => {
  return timeSlots.map(slot => {
    const {
      day_of_week: day,
      closed_all_day: closedAllDay,
      open_all_day: openAllDay,
      open_hour: openHour,
      open_minutes: openMinutes,
      close_hour: closeHour,
      close_minutes: closeMinutes,
      time_slots: timeSlotsJson,
    } = slot;

    let intervals = [];
    if (!closedAllDay) {
      const parsedIntervals = parseTimeSlotsJson(timeSlotsJson);
      intervals = parsedIntervals.length
        ? parsedIntervals.map(toInterval)
        : [{ from: getTime(openHour, openMinutes), to: getTime(closeHour, closeMinutes) }];
    }

    return {
      day,
      intervals,
      valid: openAllDay ? true : intervalsAreValid(intervals),
      openAllDay,
    };
  });
};

const intervalToPayload = interval => {
  const fromDate = parse(interval.from, 'HH:mm', new Date());
  const toDate = parse(interval.to, 'HH:mm', new Date());
  return {
    open_hour: getHours(fromDate),
    open_minutes: getMinutes(fromDate),
    close_hour: getHours(toDate),
    close_minutes: getMinutes(toDate),
  };
};

export const timeSlotTransform = timeSlots => {
  return timeSlots.map(slot => {
    const closed = slot.openAllDay ? false : !(slot.intervals && slot.intervals.length);
    const openAllDay = !!slot.openAllDay;

    if (closed) {
      return {
        day_of_week: slot.day,
        closed_all_day: true,
        open_hour: '',
        open_minutes: '',
        close_hour: '',
        close_minutes: '',
        open_all_day: false,
        time_slots: '[]',
      };
    }

    const intervals = openAllDay
      ? [{ from: '12:00 AM', to: '11:59 PM' }]
      : slot.intervals;

    const payloadIntervals = intervals.map(intervalToPayload);
    const first = payloadIntervals[0];

    return {
      day_of_week: slot.day,
      closed_all_day: false,
      open_hour: first.open_hour,
      open_minutes: first.open_minutes,
      close_hour: first.close_hour,
      close_minutes: first.close_minutes,
      open_all_day: openAllDay,
      time_slots: JSON.stringify(payloadIntervals),
    };
  });
};

export const calculateTotalHours = intervals => {
  const totalMinutes = intervals.reduce((acc, interval) => {
    const fromDate = parse(interval.from, 'HH:mm', new Date());
    const toDate = parse(interval.to, 'HH:mm', new Date());
    return acc + Math.max(0, differenceInMinutes(toDate, fromDate));
  }, 0);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return [h && `${h}h`, m && `${m}m`].filter(Boolean).join(' ') || '0m';
};

export const timeZoneOptions = () => {
  return Object.keys(timeZoneData).map(key => ({
    label: key,
    value: timeZoneData[key],
  }));
};
